import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMPAIGN_STATUS_TRANSITIONS,
  creativesInRotation,
  consentModeOf,
  CONSENT_MODES,
  type ConsentMode,
  emailAlignOf,
  EMAIL_ALIGNMENTS,
  type EmailAlign,
  distributionModelOf,
  utmZipOf,
  type CampaignStatus,
  type Creative,
  type CampaignInput,
  type CreativeInput,
  verticalLabel,
  type Vertical,
} from '@dmn/shared';
import { ArrowLeft, Copy, Eye, Image as ImageIcon, Pause as PauseIcon, Plus } from 'lucide-react';
import { CampaignCap } from '@/components/CampaignCap';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignExclusions } from '@/components/CampaignExclusions';
import { CampaignPlacements } from '@/components/CampaignPlacements';
import { CampaignVerticals } from '@/components/CampaignVerticals';
import { CreativeDialog, type CreativeCloneSeed } from '@/components/CreativeDialog';
import { CreativePlacementPreview } from '@/components/CreativePlacementPreview';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ApprovalBadge, CampaignStatusBadge } from '@/components/StatusBadge';
import { EmptyState, ErrorState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { api, ApiError } from '@/lib/api';
import { cloneName } from '@/lib/cloneName';
import { queryKeys } from '@/lib/queries';
import { formatDateTime, formatUtcDate, normalizeText } from '@/lib/utils';

export const Route = createFileRoute('/campaigns/$campaignId')({ component: CampaignDetail });

function CampaignDetail() {
  const { campaignId } = Route.useParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Creative | null>(null);
  const [previewing, setPreviewing] = useState<Creative | null>(null);
  const [creating, setCreating] = useState(false);
  const [cloning, setCloning] = useState<CreativeCloneSeed | null>(null);

  const detail = useQuery({
    queryKey: queryKeys.campaign(campaignId),
    queryFn: () => api.campaigns.get(campaignId),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.campaign(campaignId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns }),
    ]);
  };

  const setStatus = useMutation({
    mutationFn: (status: CampaignStatus) => api.campaigns.setStatus(campaignId, status),
    onSuccess: invalidate,
  });

  const setApproval = useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; approve: boolean; reason?: string }) =>
      api.creatives.setApproval(id, {
        approvalState: approve ? 'approved' : 'rejected',
        rejectionReason: reason ?? null,
      }),
    onSuccess: invalidate,
  });

  const locations = useQuery({ queryKey: queryKeys.locations, queryFn: api.locations.list });

  /**
   * Exclusions ride on the campaign's own PUT.
   *
   * The handler merges `{...existing, ...input}` and `CampaignInput` carries no
   * status, so status and every other field outside the form survive untouched —
   * which is what makes it safe to drive from this page without a full edit form.
   */
  const placementCatalogue = useQuery({
    queryKey: queryKeys.placements,
    queryFn: api.placements.list,
  });

  /**
   * Placements ride on the campaign's own PUT, exactly as exclusions do.
   * `CampaignInput` carries no status, and the handler merges over the existing
   * document, so nothing outside the form is disturbed.
   */
  /**
   * Re-sends the whole campaign with a few fields replaced.
   *
   * Every edit on this page rides on the campaign's own PUT, because
   * `CampaignInput` carries no `status` and the handler merges `{...existing,
   * ...input}` — so nothing outside the form is disturbed.
   *
   * One builder rather than a literal per editor, and that is not tidiness.
   * `CampaignInput` puts `.default()` on `caps`, `priority` and `utmCampaign`,
   * so a PUT that omits one does **not** error — it silently resets it. Three
   * hand-written copies of this object used to sit here, which meant adding a
   * campaign field meant remembering all three, and forgetting one meant the
   * next cap edit quietly wiped it. Adding the verticals editor would have made
   * four.
   */
  const patchCampaign = (
    build: (current: NonNullable<typeof detail.data>['campaign']) => Partial<CampaignInput>
  ): CampaignInput => {
    const current = detail.data?.campaign;
    if (!current) throw new Error('Campaign not loaded');
    return {
      sponsorId: current.sponsorId,
      name: current.name,
      flightStart: current.flightStart,
      flightEnd: current.flightEnd,
      utmCampaign: current.utmCampaign ?? null,
      verticals: current.verticals,
      eligibleLocationIds: current.eligibleLocationIds,
      excludedLocationIds: current.excludedLocationIds ?? [],
      placementIds: current.placementIds,
      caps: current.caps,
      // Defaulted in `CampaignInput`, so omitting it here would silently reset a
      // campaign's alignment to `center` on the next unrelated edit — exactly the
      // failure this builder exists to prevent.
      emailAlign: emailAlignOf(current),
      // Defaulted in `CampaignInput` too — omit it and an unrelated edit quietly
      // resets the campaign's consent stance to `inherit`.
      consentMode: consentModeOf(current),
      // Same trap again: defaulted in `CampaignInput`, so omitting it resets the
      // campaign's distribution model on the next unrelated edit.
      distributionModel: distributionModelOf(current),
      // And again, with a worse failure: omitting it would quietly stop sending a
      // ZIP somebody deliberately agreed to send — or, read the other way, this
      // builder is the only thing that can turn it on, so it must say what it is.
      utmZip: utmZipOf(current),
      priority: current.priority,
      ...build(current),
    };
  };

  const setCap = useMutation({
    mutationFn: (totalImpressions: number) =>
      api.campaigns.update(
        campaignId,
        patchCampaign((current) => ({ caps: { ...current.caps, totalImpressions } }))
      ),
    onSuccess: invalidate,
  });

  const setPlacements = useMutation({
    mutationFn: (placementIds: string[]) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ placementIds }))
      ),
    onSuccess: invalidate,
  });

  const setExclusions = useMutation({
    mutationFn: (excludedLocationIds: string[]) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ excludedLocationIds }))
      ),
    onSuccess: invalidate,
  });

  /**
   * Verticals decide which practices a campaign may reach at all, so this is the
   * edit most able to take a live campaign to zero delivery. The server enforces
   * `min(1)`; the card refuses to save an empty selection before that, and warns
   * when the chosen specialties match no active practice — which otherwise looks
   * like weak demand rather than a targeting mistake.
   */
  const setVerticals = useMutation({
    mutationFn: (verticals: Vertical[]) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ verticals }))
      ),
    onSuccess: invalidate,
  });

  /**
   * Layout, not targeting, so it saves on change rather than behind a Save button
   * — there is no intermediate state the schema forbids and nothing to batch.
   */
  const setEmailAlign = useMutation({
    mutationFn: (emailAlign: EmailAlign) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ emailAlign }))
      ),
    onSuccess: invalidate,
  });

  const setConsentMode = useMutation({
    mutationFn: (consentMode: ConsentMode) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ consentMode }))
      ),
    onSuccess: invalidate,
  });

  const setUtmZip = useMutation({
    mutationFn: (utmZip: boolean) =>
      api.campaigns.update(
        campaignId,
        patchCampaign(() => ({ utmZip }))
      ),
    onSuccess: invalidate,
  });

  const setServing = useMutation({
    mutationFn: ({ id, pause }: { id: string; pause: boolean }) =>
      api.creatives.setServing(id, { servingState: pause ? 'paused' : 'active' }),
    onSuccess: invalidate,
  });

  const saveCreative = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: CreativeInput }) =>
      id ? api.creatives.update(id, input) : api.creatives.create(input),
    onSuccess: async () => {
      setCreating(false);
      setEditing(null);
      setCloning(null);
      await invalidate();
    },
  });

  if (detail.isError)
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;

  const campaign = detail.data?.campaign;
  const creatives = detail.data?.creatives ?? [];
  // Which creatives could serve this instant, by the same rule the decision path
  // uses. Shown per creative below, because "4 creatives, 1 serving" is
  // impossible to work out from approval state and version alone.
  const rotationIds = new Set(creativesInRotation(creatives).map((creative) => creative.id));
  const allowedTransitions = campaign ? CAMPAIGN_STATUS_TRANSITIONS[campaign.status] : [];

  return (
    <>
      <PageHeader
        title={campaign?.name ?? 'Campaign'}
        description={campaign ? `${campaign.sponsorName} · ${campaign.id}` : undefined}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/campaigns">
              <ArrowLeft className="size-4" />
              All campaigns
            </Link>
          </Button>
        }
      />
      <PageBody>
        {detail.isPending && <Skeleton className="h-40 w-full" />}

        {campaign && (
          <>
            <Card className="shadow-none">
              <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
                  <Field label="Status">
                    <CampaignStatusBadge status={campaign.status} />
                  </Field>
                  <Field label="Flight">
                    {formatUtcDate(campaign.flightStart)} – {formatUtcDate(campaign.flightEnd)}
                  </Field>
                  <Field label="Verticals">
                    {campaign.verticals.map((v) => verticalLabel(v)).join(', ')}
                  </Field>
                  <Field label="Frequency cap">
                    {campaign.caps.perPersonPerWindow || '∞'} / {campaign.caps.windowHours}h
                    {campaign.caps.perPersonTotal > 0 &&
                      ` · ${campaign.caps.perPersonTotal} lifetime`}
                  </Field>
                  <Field label="Priority">{campaign.priority}</Field>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {allowedTransitions.length === 0 && (
                    <span className="text-xs text-muted-foreground">No transitions available</span>
                  )}
                  {allowedTransitions.map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={status === 'active' ? 'default' : 'outline'}
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate(status)}
                    >
                      {transitionLabel(status)}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {setStatus.isError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {setStatus.error instanceof ApiError
                  ? setStatus.error.message
                  : 'Status change failed'}
              </p>
            )}

            <CampaignCap
              campaign={campaign}
              pending={setCap.isPending}
              error={setCap.error}
              onSave={(total) => setCap.mutate(total)}
            />

            <CampaignPlacements
              placementIds={campaign.placementIds}
              placements={placementCatalogue.data ?? []}
              creatives={creatives}
              pending={setPlacements.isPending}
              error={setPlacements.error}
              onSave={(ids) => setPlacements.mutate(ids)}
            />

            <CampaignVerticals
              verticals={campaign.verticals}
              locations={locations.data ?? []}
              locationsLoading={locations.isPending}
              pending={setVerticals.isPending}
              error={setVerticals.error}
              onSave={(next) => setVerticals.mutate(next)}
            />

            <CampaignExclusions
              campaign={campaign}
              locations={locations.data ?? []}
              pending={setExclusions.isPending}
              onChange={(ids) => setExclusions.mutate(ids)}
            />

            <Card className="shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Consent</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <Select
                  value={consentModeOf(campaign)}
                  disabled={setConsentMode.isPending}
                  onValueChange={(next) => setConsentMode.mutate(next as ConsentMode)}
                >
                  <SelectTrigger className="max-w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONSENT_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  <code className="font-mono">inherit</code> follows the placement.{' '}
                  <code className="font-mono">required</code> asks even where the placement waives
                  it. <code className="font-mono">exempt</code> serves people who have not answered.
                  Practice surfaces are never asked, and anyone who declined or revoked is excluded
                  either way.
                </p>
                {consentModeOf(campaign) === 'exempt' && (
                  <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
                    Serving to patients who never opted in.
                  </p>
                )}
                {setConsentMode.isError && (
                  <p className="text-sm text-destructive">
                    {setConsentMode.error instanceof ApiError
                      ? setConsentMode.error.message
                      : 'Could not change consent mode'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/*
              Read-only on purpose: there is one model, and a Select with a
              single option reads as a half-finished feature. It is shown at all
              because an operator adding a second creative needs to know what
              happens next — and because the field is on the document and in the
              audit trail, so the page should not be the one place it is invisible.
            */}
            <Card className="shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Creative distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <p className="text-sm font-medium">Random — even rotation</p>
                <p className="text-xs text-muted-foreground">
                  Every approved creative that is not paused takes a share of delivery, picked
                  independently on each impression. Pause a creative to take it out. The sponsor
                  stays put for a given person — only which of their creatives shows varies.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Email layout</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <Select
                  value={emailAlignOf(campaign)}
                  disabled={setEmailAlign.isPending}
                  onValueChange={(next) => setEmailAlign.mutate(next as EmailAlign)}
                >
                  <SelectTrigger className="max-w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_ALIGNMENTS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Where the unit sits in the host email. At most 600px wide, so in a wider message
                  the spare room goes somewhere — <code className="font-mono">center</code> is the
                  old behaviour and can read as an odd float beside left-aligned body copy. No
                  effect on web placements.
                </p>
                {setEmailAlign.isError && (
                  <p className="text-sm text-destructive">
                    {setEmailAlign.error instanceof ApiError
                      ? setEmailAlign.error.message
                      : 'Could not change alignment'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Off by default and worded as a disclosure, not a feature: turning it on
                sends something about the person who clicked to the sponsor. */}
            <Card className="shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">ZIP in sponsor links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <div className="flex items-start justify-between gap-4">
                  <p className="text-xs text-muted-foreground">
                    Adds the practice&apos;s ZIP to every click as{' '}
                    <code className="font-mono">utm_zip</code>. The sponsor sees it in their own
                    analytics, next to that visitor — where a ZIP has one practice of this
                    specialty, that tells them where the person is a patient. Turn on only with
                    compliance sign-off for this sponsor. Links already sent keep what they were
                    sent with. Not a standard UTM field: the sponsor&apos;s analytics only records
                    it if they set it up to.
                  </p>
                  <Switch
                    aria-label="ZIP in sponsor links"
                    checked={utmZipOf(campaign)}
                    disabled={setUtmZip.isPending}
                    onCheckedChange={(checked) => setUtmZip.mutate(checked)}
                  />
                </div>
                {setUtmZip.isError && (
                  <p className="text-sm text-destructive">
                    {setUtmZip.error instanceof ApiError
                      ? setUtmZip.error.message
                      : 'Could not change this setting'}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="hidden">
              <CardContent className="pt-0">
                {setExclusions.isError && (
                  <p className="text-sm text-destructive">
                    {setExclusions.error instanceof ApiError
                      ? setExclusions.error.message
                      : 'Could not change exclusions'}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium">
                  Creatives
                  <span className="ml-2 font-normal text-muted-foreground">
                    {campaign.approvedCreativeCount} approved of {creatives.length}
                  </span>
                </CardTitle>
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus className="size-4" />
                  New creative
                </Button>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {creatives.length === 0 && (
                  <EmptyState
                    title="No creatives yet"
                    description="A campaign cannot go live until one creative is approved."
                    icon={ImageIcon}
                  />
                )}
                {creatives.map((creative) => (
                  <div key={creative.id} className="rounded-lg border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{creative.name}</span>
                          <Badge variant="secondary" className="font-mono text-xs">
                            v{creative.version}
                          </Badge>
                          <ApprovalBadge state={creative.approvalState} />
                          {creative.servingState === 'paused' && (
                            <Badge variant="outline" className="text-muted-foreground">
                              <PauseIcon className="mr-1 size-3" />
                              Paused
                            </Badge>
                          )}
                          {rotationIds.has(creative.id) && (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600">
                              Serving now
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium">{creative.fields.headline}</p>
                        <p className="max-w-2xl text-sm text-muted-foreground">
                          {creative.fields.body}
                        </p>
                        {creative.fields.safetyText && (
                          <p className="max-w-2xl text-xs text-muted-foreground italic">
                            {creative.fields.safetyText}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {creative.approvalState === 'approved'
                            ? `Approved by ${creative.approvedBy} · ${formatDateTime(creative.approvedAt)}`
                            : creative.rejectionReason
                              ? `Rejected: ${creative.rejectionReason}`
                              : `Updated ${formatDateTime(creative.updatedAt)}`}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setPreviewing(creative)}>
                          <Eye className="size-4" />
                          Preview
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(creative)}>
                          Edit
                        </Button>
                        {/*
                          Opens the editor prefilled rather than duplicating on
                          the spot: the reason to clone is almost always to change
                          something, and nothing is written if the operator backs
                          out. The copy is named distinctly from its siblings
                          because that name labels the creative in by-creative
                          delivery reporting, which sponsors read.
                        */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setCloning({
                              sourceId: creative.id,
                              name: cloneName(
                                creative.name,
                                creatives.map((sibling) => sibling.name)
                              ),
                              fields: creative.fields,
                            })
                          }
                        >
                          <Copy className="size-4" />
                          Clone
                        </Button>
                        {creative.approvalState !== 'approved' && (
                          <Button
                            size="sm"
                            disabled={setApproval.isPending}
                            onClick={() => setApproval.mutate({ id: creative.id, approve: true })}
                          >
                            Approve
                          </Button>
                        )}
                        {creative.approvalState === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={setServing.isPending}
                            onClick={() =>
                              setServing.mutate({
                                id: creative.id,
                                pause: creative.servingState !== 'paused',
                              })
                            }
                          >
                            {creative.servingState === 'paused' ? 'Resume' : 'Pause'}
                          </Button>
                        )}
                        {creative.approvalState !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={setApproval.isPending}
                            onClick={() => {
                              const reason = window.prompt('Reason for rejection');
                              if (reason) {
                                setApproval.mutate({ id: creative.id, approve: false, reason });
                              }
                            }}
                          >
                            Reject
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {setServing.isError && (
                  <p className="text-sm text-destructive">
                    {setServing.error instanceof ApiError
                      ? setServing.error.message
                      : 'Could not change rotation'}
                  </p>
                )}
                {setApproval.isError && (
                  <p className="text-sm text-destructive">
                    {setApproval.error instanceof ApiError
                      ? setApproval.error.message
                      : 'Approval change failed'}
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>

      <CreativeDialog
        open={creating || editing !== null || cloning !== null}
        campaignId={campaignId}
        placementIds={campaign?.placementIds ?? []}
        creative={editing}
        cloneFrom={cloning}
        pending={saveCreative.isPending}
        error={saveCreative.error}
        // `editing` is null in clone mode, so this creates a separate creative
        // rather than forking a version of the one that was copied.
        onSave={(input) => saveCreative.mutate({ id: editing?.id ?? null, input })}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
            setCloning(null);
            saveCreative.reset();
          }
        }}
      />

      <Dialog open={previewing !== null} onOpenChange={(open) => !open && setPreviewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewing?.name ?? 'Creative preview'}</DialogTitle>
            <DialogDescription>
              Illustrative preview of how this creative appears in each selected placement.
            </DialogDescription>
          </DialogHeader>
          {previewing && (
            <CreativePlacementPreview
              fields={previewing.fields}
              placementIds={campaign?.placementIds ?? []}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs tracking-wide text-muted-foreground uppercase">{label}</p>
      <div className="flex items-center gap-2 font-medium">{children}</div>
    </div>
  );
}

const TRANSITION_LABELS: Partial<Record<CampaignStatus, string>> = {
  pending_approval: 'Submit for approval',
  active: 'Go live',
  paused: 'Pause',
  completed: 'Mark complete',
  archived: 'Archive',
  draft: 'Return to draft',
};

const transitionLabel = (status: CampaignStatus): string =>
  TRANSITION_LABELS[status] ?? normalizeText(status);
